import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Users, Building2, MapPin, TrendingUp, Download, ExternalLink } from 'lucide-react';
import { toast } from "react-toastify";

const GrantsDashboard = () => {
  const [expandedMicroSection, setExpandedMicroSection] = useState(null);
  const [expandedSmeEoi, setExpandedSmeEoi] = useState(null);
  const [expandedCfpBatch, setExpandedCfpBatch] = useState(null);

  const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';

  const implementingPartners = [
    { id: 'pmn', name: 'PMN' },
    { id: 'srso', name: 'SRSO' },
    { id: 'act-intl', name: 'ACT INTL' },
    { id: 'nrsp', name: 'NRSP' },
    { id: 'trdp', name: 'TRDP' }
  ];

  const partnerDocuments = {
    weeklyReports: 'Weekly Reports',
    beneficiariesList: 'Beneficiaries List',
    grantAgreement: 'Grant Agreement',
    qprs: 'QPRs',
    inceptionReport: 'Inception Report',
    financialReports: 'Financial Reports',
    adsCall: 'Ads – Call for Applications'
  };

  const eois = [
    { 
      id: 6, 
      name: '6th EOI',
      documents: {
        qualificationReview: null,
        technicalFinancialEvaluation: null,
        minutesOfQualification: null,
        successfulApplicants: null,
        rfpTors: null,
        eoiResponses: null
      }
    },
    { 
      id: 5, 
      name: '5th EOI',
      documents: {
        qualificationReview: null,
        technicalFinancialEvaluation: null,
        minutesOfQualification: null,
        successfulApplicants: null,
        rfpTors: null,
        eoiResponses: null
      }
    },
    { 
      id: 4, 
      name: '4th EOI',
      documents: {
        qualificationReview: null,
        technicalFinancialEvaluation: null,
        minutesOfQualification: null,
        successfulApplicants: null,
        rfpTors: null,
        eoiResponses: null
      }
    },
    { 
      id: 3, 
      name: '3rd EOI',
      documents: {
        qualificationReview: null,
        technicalFinancialEvaluation: null,
        minutesOfQualification: null,
        successfulApplicants: null,
        rfpTors: `${BASE_URL}/uploads/grants/sme_grants/eoi_documents/7000007483_3rd CEI_SME Grants_PAIDAR.pdf`,
        eoiResponses: null
      }
    },
    { 
      id: 2, 
      name: '2nd EOI',
      documents: {
        qualificationReview: `${BASE_URL}/uploads/grants/sme_grants/eoi_documents/2nd Call for EOIs-Preliminary Qualification Review Sheet - FINAL 25092024.xls`,
        technicalFinancialEvaluation: null,
        minutesOfQualification: null,
        successfulApplicants: null,
        rfpTors: `${BASE_URL}/uploads/grants/sme_grants/eoi_documents/7000007038_2nd Call for Expression of Interest *SME Grants*PAIDAR .pdf`,
        eoiResponses: null
      }
    },
    { 
      id: 1, 
      name: '1st EOI',
      documents: {
        qualificationReview: null,
        technicalFinancialEvaluation: `${BASE_URL}/uploads/grants/sme_grants/evaluation_documents/Database of Approved SME Grant Cases Final.xlsx`,
        minutesOfQualification: null,
        successfulApplicants: `${BASE_URL}/uploads/grants/sme_grants/evaluation_documents/List of 41 Recommended Grantees.xlsx`,
        rfpTors: null,
        eoiResponses: null
      }
    }
  ];

  const eoiDocumentLabels = {
    qualificationReview: 'Qualification Review',
    technicalFinancialEvaluation: 'Technical & Financial Evaluation',
    minutesOfQualification: 'Minutes of Qualification',
    successfulApplicants: 'Successful Applicants',
    rfpTors: 'RFP & TORs',
    eoiResponses: 'EOI Responses'
  };

  const cfpBatch1Documents = {
    grantAgreements: null,
    milestones: null,
    rfpTors: `${BASE_URL}/uploads/grants/sme_grants/cfp_documents/7000007031_Restricted Call for Proposal- Business Dev. Grants for SMEs.pdf`,
    evaluationSheets: null,
    proposals: null,
    batchContactSheet: `${BASE_URL}/uploads/grants/sme_grants/evaluation_documents/18 Grantees for cheque distribution.xlsx`,
    regretLetters: null,
    termsOfReference: `${BASE_URL}/uploads/grants/sme_grants/cfp_documents/7000007031 _TERMS OF REFERENCE .pdf`
  };

  const cfpBatchDocumentLabels = {
    grantAgreements: 'Grant Agreements',
    milestones: 'Milestones',
    rfpTors: 'RFP & TORs',
    evaluationSheets: 'Evaluation Sheets',
    proposals: 'Proposals',
    batchContactSheet: 'Batch 1 Contact Sheet',
    regretLetters: 'Regret Letters',
    termsOfReference: 'Terms of Reference'
  };

  const toggleMicroSection = (section) => {
    setExpandedMicroSection(expandedMicroSection === section ? null : section);
  };

  const toggleSmeEoi = (eoiId) => {
    setExpandedSmeEoi(expandedSmeEoi === eoiId ? null : eoiId);
  };

  const toggleCfpBatch = () => {
    setExpandedCfpBatch(expandedCfpBatch ? null : true);
  };

  const openFile = (fileUrl) => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const usefulAttachments = [
    { 
      name: 'Grants Manual', 
      icon: FileText,
      url: `${BASE_URL}/uploads/grants/general/useful_attachments/UNIDO Grants_Manual.pdf`
    },
    { 
      name: 'PAIDAR Inception Report', 
      icon: FileText,
      url: `${BASE_URL}/uploads/grants/general/useful_attachments/Inception Report PAIDAR_Final.pdf`
    },
    { 
      name: 'Sindh Poverty Reduction Strategy', 
      icon: FileText,
      url: `${BASE_URL}/uploads/grants/general/useful_attachments/Sindh Poverty Reduction Strategy.pdf`
    },
    { name: 'Grant Award Process Flow', icon: TrendingUp, url: null },
    { name: 'Grant Application Process Flow', icon: TrendingUp, url: null },
    { name: 'PAIDAR Regret Mechanism', icon: FileText, url: null },
    { name: 'PAIDAR Grievance Redressal Mechanism (GRM)', icon: FileText, url: null },
    { name: 'PAIDAR Grants Due-Diligence Form', icon: FileText, url: null },
    { name: 'PAIDAR Grants Brief', icon: FileText, url: null },
    { name: 'PAIDAR Grants Mission Reports', icon: FileText, url: null },
    { name: 'Technical Assistance to Grantees', icon: Users, url: null },
    { name: 'Micro-Grants', icon: Building2, url: null },
    { name: 'Communal Infrastructure Grants', icon: MapPin, url: null }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">        
        {/* Useful Attachments Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600" />
            Useful Attachments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {usefulAttachments.map((attachment, index) => {
              const IconComponent = attachment.icon;
              return (
                <button
                  key={index}
                  onClick={() => attachment.url ? openFile(attachment.url) : toast.error('Document not available yet')}
                  className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200 h-12 ${
                    attachment.url 
                      ? 'hover:bg-teal-50 hover:border-teal-300 text-gray-700 hover:text-teal-700 cursor-pointer' 
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <IconComponent className={`h-4 w-4 flex-shrink-0 ${attachment.url ? 'text-teal-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium truncate">{attachment.name}</span>
                  {attachment.url && <ExternalLink className="h-3 w-3 ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Grant Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Micro-Grants Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-600" />
              Micro-Grants
            </h2>
            
            <div className="space-y-3">
              {/* RFP & TORs */}
              <div>
                <button 
                  onClick={() => toggleMicroSection('rfp-tors')} 
                  className={`flex items-center justify-between w-full p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-200 ${
                    expandedMicroSection === 'rfp-tors' ? 'border-teal-300 bg-teal-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-teal-600" />
                    <span className="font-medium text-gray-700">RFP & TORs</span>
                  </div>
                  {expandedMicroSection === 'rfp-tors' ? 
                    <ChevronDown className="h-4 w-4 text-gray-500" /> : 
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  }
                </button>
                {expandedMicroSection === 'rfp-tors' && (
                  <div className="mt-2 ml-6 space-y-2">
                    <button 
                      onClick={() => toast.error('Document not available yet')}
                      className="flex items-center gap-2 text-gray-400 text-sm p-2 rounded cursor-not-allowed"
                    >
                      <FileText className="h-3 w-3" />
                      Request for Proposals Document
                    </button>
                    <button 
                      onClick={() => toast.error('Document not available yet')}
                      className="flex items-center gap-2 text-gray-400 text-sm p-2 rounded cursor-not-allowed"
                    >
                      <FileText className="h-3 w-3" />
                      Terms of Reference
                    </button>
                  </div>
                )}
              </div>
              
              {/* IPs Proposals */}
              <div>
                <button 
                  onClick={() => toggleMicroSection('ips-proposals')} 
                  className={`flex items-center justify-between w-full p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-200 ${
                    expandedMicroSection === 'ips-proposals' ? 'border-teal-300 bg-teal-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-teal-600" />
                    <span className="font-medium text-gray-700">IPs Proposals</span>
                  </div>
                  {expandedMicroSection === 'ips-proposals' ? 
                    <ChevronDown className="h-4 w-4 text-gray-500" /> : 
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  }
                </button>
                {expandedMicroSection === 'ips-proposals' && (
                  <div className="mt-2 ml-6 space-y-2">
                    {implementingPartners.map(partner => (
                      <button 
                        key={partner.id} 
                        onClick={() => toast.error('Document not available yet')}
                        className="flex items-center gap-2 text-gray-400 text-sm p-2 rounded cursor-not-allowed"
                      >
                        <FileText className="h-3 w-3" />
                        Proposal from {partner.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Implementing Partners */}
              <div className="pt-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4 text-teal-600" />
                  Implementing Partners
                </h3>
                <div className="space-y-2">
                  {implementingPartners.map(partner => (
                    <div key={partner.id}>
                      <button 
                        onClick={() => toggleMicroSection(`partner-${partner.id}`)} 
                        className={`flex items-center justify-between w-full p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-200 ${
                          expandedMicroSection === `partner-${partner.id}` ? 'border-teal-300 bg-teal-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-teal-600" />
                          <span className="font-medium text-gray-700">{partner.name}</span>
                        </div>
                        {expandedMicroSection === `partner-${partner.id}` ? 
                          <ChevronDown className="h-4 w-4 text-gray-500" /> : 
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        }
                      </button>
                      {expandedMicroSection === `partner-${partner.id}` && (
                        <div className="mt-2 ml-6 space-y-2">
                          {Object.values(partnerDocuments).map((doc, index) => (
                            <button 
                              key={index} 
                              onClick={() => toast.error('Document not available yet')}
                              className="flex items-center gap-2 text-gray-400 text-sm p-2 rounded cursor-not-allowed"
                            >
                              <FileText className="h-3 w-3" />
                              {doc}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* SME Grants Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-teal-600" />
              SME Business Development Grants
            </h2>
            
            <div className="space-y-2">
              {eois.map(eoi => (
                <div key={eoi.id}>
                  <button 
                    onClick={() => toggleSmeEoi(eoi.id)} 
                    className={`flex items-center justify-between w-full p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-200 ${
                      expandedSmeEoi === eoi.id ? 'border-teal-300 bg-teal-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-teal-600" />
                      <span className="font-medium text-gray-700">{eoi.name}</span>
                    </div>
                    {expandedSmeEoi === eoi.id ? 
                      <ChevronDown className="h-4 w-4 text-gray-500" /> : 
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    }
                  </button>
                  {expandedSmeEoi === eoi.id && (
                    <div className="mt-2 ml-6">
                      <div className="space-y-2 mb-3">
                        {Object.entries(eoiDocumentLabels).map(([key, label]) => {
                          const documentUrl = eoi.documents[key];
                          return (
                            <button 
                              key={key} 
                              onClick={() => documentUrl ? openFile(documentUrl) : toast.error('Document not available yet')}
                              className={`flex items-center gap-2 text-sm p-2 rounded transition-colors duration-200 ${
                                documentUrl 
                                  ? 'text-teal-600 hover:text-teal-700 hover:bg-teal-50 cursor-pointer' 
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                            >
                              <FileText className="h-3 w-3" />
                              {label}
                              {documentUrl && <ExternalLink className="h-3 w-3 ml-auto" />}
                            </button>
                          );
                        })}
                      </div>
                      {eoi.id === 1 && (
                        <div className="mt-3">
                          <button 
                            onClick={toggleCfpBatch} 
                            className={`flex items-center justify-between w-full p-3 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors duration-200 ${
                              expandedCfpBatch ? 'border-teal-300 bg-teal-100' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-teal-600" />
                              <span className="font-medium text-gray-700">Restricted CFP Batch 1</span>
                            </div>
                            {expandedCfpBatch ? 
                              <ChevronDown className="h-4 w-4 text-gray-500" /> : 
                              <ChevronRight className="h-4 w-4 text-gray-500" />
                            }
                          </button>
                          {expandedCfpBatch && (
                            <div className="mt-2 ml-6 space-y-2">
                              {Object.entries(cfpBatchDocumentLabels).map(([key, label]) => {
                                const documentUrl = cfpBatch1Documents[key];
                                return (
                                  <button 
                                    key={key} 
                                    onClick={() => documentUrl ? openFile(documentUrl) : toast.error('Document not available yet')}
                                    className={`flex items-center gap-2 text-sm p-2 rounded transition-colors duration-200 ${
                                      documentUrl 
                                        ? 'text-teal-600 hover:text-teal-700 hover:bg-teal-50 cursor-pointer' 
                                        : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                  >
                                    <FileText className="h-3 w-3" />
                                    {label}
                                    {documentUrl && <ExternalLink className="h-3 w-3 ml-auto" />}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrantsDashboard;